import { useCallback, useEffect, useRef } from "react";
import { IgnoreClickOutsideAttribute } from "../constants/index.js";
import { ExternalUtils } from "../utils/index.js";

const findIfIgnoredByRules = rules => async event => {
  if (!rules) return false;

  let ignored = false,
    index = 0;

  while (!ignored && index < rules.length) {
    ignored = await rules[index](event);
    index++;
  }

  return ignored;
};

const blockEvents = event => {
  event.preventDefault();
  event.stopPropagation();
};

const temporaryBlockEventsForTablet = () => {
  document.addEventListener("touchstart", blockEvents, true);
  document.addEventListener("touchend", blockEvents, true);
  setTimeout(() => {
    document.removeEventListener("touchstart", blockEvents, true);
    document.removeEventListener("touchend", blockEvents, true);
  }, 100);
};

export const useClickOutside = (callback, options = {}) => {
  const ref = useRef(null);
  const { disabled, ignoreClass, refs } = options;

  const wrappedCallback = useCallback(
    async event => {
      temporaryBlockEventsForTablet();
      const isIgnored = await findIfIgnoredByRules(ignoreRulesList)(event);
      if (isIgnored) return;

      const isValidCallbackFn = typeof callback === "function";
      if (isValidCallbackFn) callback(event);
    },
    [callback]
  );

  useEffect(() => {
    if (disabled) return;

    const handler = event => {
      // Check the main ref
      if (ref.current && ref.current.contains(event.target)) return;

      // Check additional refs
      if (refs?.length) {
        const isInsideRef = refs.some(
          r => r.current && r.current.contains(event.target)
        );
        if (isInsideRef) return;
      }

      // Check ignoreClass (supports string or array of class names)
      const ignoreClasses = Array.isArray(ignoreClass)
        ? ignoreClass
        : [ignoreClass];
      const shouldIgnore = ignoreClasses.filter(Boolean).some(className => {
        if (typeof event?.target?.closest !== "function") return false;
        return Boolean(event.target.closest(`.${className}`));
      });
      if (shouldIgnore) return;

      wrappedCallback(event);
    };
    document.addEventListener("mousedown", handler, true);
    document.addEventListener("touchstart", handler, true);
    return () => {
      document.removeEventListener("mousedown", handler, true);
      document.removeEventListener("touchstart", handler, true);
    };
  }, [wrappedCallback, disabled, ignoreClass, refs]);

  return ref;
};

const defaultIgnoreRule = event => {
  const containers = document.querySelectorAll(
    `[${IgnoreClickOutsideAttribute}]`
  );

  if (!containers || containers.length === 0) return false;

  return Array.from(containers).some(container => {
    return container.contains(event?.target);
  });
};

const cookieMessageIgnoreRule = async event => {
  const cookieBannerContainerId =
    await ExternalUtils.UCSDKService.getB2CCookieContainerIdAsync();
  if (!cookieBannerContainerId) return false;

  const cookieBannerContainer = document.getElementById(
    cookieBannerContainerId
  );
  if (!cookieBannerContainer) return false;

  return cookieBannerContainer.contains(event?.target);
};

const ignoreRulesList = [defaultIgnoreRule, cookieMessageIgnoreRule];
